/* eslint-disable max-len */
import { Component, Element, h, Prop, Listen, State } from '@stencil/core';
import { versionComponent } from '../../global/inject-dependencies';

@Component({
  tag: 'ui-button',
  styleUrl: 'ui-button.scss',
  shadow: true,
})
export class UiButton {
  @Element() el: HTMLElement;

  /**
   * defines the input's type. An input can have various purposes: as a **checkbox**, a **radio**, a **date**, etc. Input elements of type submit are rendered
   * as buttons.
   */
  @Prop() type: 'submit';

  /**
   * refers to the general look and style of the button. The three available looks - **primary**, **secondary** and **cta** - grant
   * the designers and developers a wider range of styling options.
   */
  @Prop({ reflect: true }) variant:
    | 'primary'
    | 'secondary'
    | 'cta'
    | 'plain'
    | 'transparent' = 'primary';

  @Prop() icon: string;

  /**
   * allows the button to have two modes: enabled or disabled until a certain requirement is met.
   */
  @Prop({ reflect: true }) disabled: boolean;

  /**
   * Defines the aria-label of the button element
   * The value comes from the buttonAriaLabel property, the slot or the icon name.
   */
  @State() ariaLabel: string;

  private fakeSubmit(event: Event) {
    const form = this.el.closest('form');
    if (form) {
      event.preventDefault();
      const fakeSubmit = document.createElement('button');
      fakeSubmit.type = 'submit';
      fakeSubmit.style.display = 'none';
      form.appendChild(fakeSubmit);
      fakeSubmit.click();
      fakeSubmit.remove();
    }
  }

  @Listen('click', {
    capture: true,
  })
  onClick(event: MouseEvent) {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.type === 'submit') {
      this.fakeSubmit(event);
    }
  }

  setAriaLabel() {
    this.ariaLabel =
      this.el.getAttribute('aria-label') || this.el.innerText || this.icon;
  }

  componentWillLoad() {
    this.setAriaLabel();
  }

  render() {
    return (
      <button disabled={this.disabled} aria-label={this.ariaLabel}>
        {this.icon && <ui-icon icon={this.icon} type="system"></ui-icon>}
        <span class="button__text">
          <slot onSlotchange={this.setAriaLabel.bind(this)}></slot>
        </span>
      </button>
    );
  }
}

versionComponent(UiButton);
